import { resolveStoredFileUrl } from "utils/storedFileUrl";
import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";
import SoftBox from "components/SoftBox";
import Mesa from "../Mesa";

const MESA_DIAMETER = 70;
const MESA_STATUS_OFFSET = 24;
const CANVAS_PADDING = 56;
const PAN_START_THRESHOLD = 4;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

function PlanViewport({
  areaActual,
  areaTienePlano,
  mesas,
  mesasTemporal,
  moverHabilitado,
  setMesasTemporal,
  handleOpenPedido,
  zoom = 1,
  mobile = false,
  tablet = false,
}) {
  const viewportRef = useRef(null);
  const panSessionRef = useRef(null);
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });
  const [imageRatio, setImageRatio] = useState(null);
  const [isPanning, setIsPanning] = useState(false);
  const safeZoom = Number.isFinite(Number(zoom)) ? Number(zoom) : 1;

  const mesasLayout = mesasTemporal?.length ? mesasTemporal : mesas;

  const canvasMetrics = useMemo(() => {
    const layout = Array.isArray(mesasLayout) ? mesasLayout : [];
    const maxX = layout.reduce((acc, mesa) => Math.max(acc, Number(mesa?.posicionX) || 0), 0);
    const maxY = layout.reduce((acc, mesa) => Math.max(acc, Number(mesa?.posicionY) || 0), 0);
    const widthFromMesas = maxX + MESA_DIAMETER + CANVAS_PADDING;
    const heightFromMesas = maxY + MESA_DIAMETER + MESA_STATUS_OFFSET + CANVAS_PADDING;
    const width = Math.max(widthFromMesas, viewportSize.width || 0);
    const imageHeight = imageRatio ? Math.round(width * imageRatio) : 0;

    return {
      width,
      height: Math.max(heightFromMesas, imageHeight, viewportSize.height || 0),
    };
  }, [imageRatio, mesasLayout, viewportSize.height, viewportSize.width]);

  const scaledCanvasMetrics = useMemo(
    () => ({
      width: Math.round(canvasMetrics.width * safeZoom),
      height: Math.round(canvasMetrics.height * safeZoom),
    }),
    [canvasMetrics.height, canvasMetrics.width, safeZoom]
  );

  const maxScroll = useMemo(
    () => ({
      left: Math.max(scaledCanvasMetrics.width - (viewportSize.width || 0), 0),
      top: Math.max(scaledCanvasMetrics.height - (viewportSize.height || 0), 0),
    }),
    [scaledCanvasMetrics.height, scaledCanvasMetrics.width, viewportSize.height, viewportSize.width]
  );

  const canPan = !moverHabilitado && (maxScroll.left > 0 || maxScroll.top > 0);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;

    const updateViewportSize = () => {
      setViewportSize({
        width: viewport.clientWidth,
        height: viewport.clientHeight,
      });
    };

    updateViewportSize();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(() => {
        updateViewportSize();
      });
      observer.observe(viewport);
      return () => {
        observer.disconnect();
      };
    }

    window.addEventListener("resize", updateViewportSize);

    return () => {
      window.removeEventListener("resize", updateViewportSize);
    };
  }, []);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    viewport.scrollLeft = clamp(viewport.scrollLeft, 0, maxScroll.left);
    viewport.scrollTop = clamp(viewport.scrollTop, 0, maxScroll.top);
  }, [maxScroll.left, maxScroll.top]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    viewport.scrollLeft = 0;
    viewport.scrollTop = 0;
    setImageRatio(null);
    setIsPanning(false);
  }, [areaActual?.id, mobile, tablet]);

  const handleViewportPointerDown = (event) => {
    if (!canPan) return;

    if (event.pointerType === "mouse" && event.button !== 0) {
      return;
    }

    const target = event.target;
    if (target?.closest?.("[data-mesa-interactive='true']")) {
      return;
    }

    const viewport = viewportRef.current;
    if (!viewport) return;

    panSessionRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startScrollLeft: viewport.scrollLeft,
      startScrollTop: viewport.scrollTop,
      moved: false,
    };

    viewport.setPointerCapture?.(event.pointerId);
  };

  const handleViewportPointerMove = (event) => {
    const viewport = viewportRef.current;
    const panSession = panSessionRef.current;

    if (!viewport || !panSession) return;

    const deltaX = event.clientX - panSession.startX;
    const deltaY = event.clientY - panSession.startY;

    if (!panSession.moved && Math.hypot(deltaX, deltaY) < PAN_START_THRESHOLD) {
      return;
    }

    panSession.moved = true;
    setIsPanning(true);

    viewport.scrollLeft = clamp(panSession.startScrollLeft - deltaX, 0, maxScroll.left);
    viewport.scrollTop = clamp(panSession.startScrollTop - deltaY, 0, maxScroll.top);
    event.preventDefault();
  };

  const finishViewportPointerPan = (event) => {
    const viewport = viewportRef.current;
    const panSession = panSessionRef.current;

    if (!viewport || !panSession) return;

    if (event?.pointerId !== undefined && viewport.hasPointerCapture?.(event.pointerId)) {
      viewport.releasePointerCapture(event.pointerId);
    }

    panSessionRef.current = null;
    setIsPanning(false);
  };

  const canvasMinHeight = mobile ? "68vh" : tablet ? "72vh" : "auto";
  const viewportMinHeight = mobile ? "66vh" : tablet ? "72vh" : { xs: "60vh", md: "68vh", lg: "72vh" };

  return (
    <SoftBox
      sx={{
        p: tablet ? 0.75 : mobile ? 0.85 : { xs: 1, md: 1.5 },
        minHeight: canvasMinHeight,
        borderRadius: "1.5rem",
        background: "#ffffff",
        border: "1px solid rgba(148, 163, 184, 0.22)",
      }}
    >
      <SoftBox
        ref={viewportRef}
        onPointerDown={handleViewportPointerDown}
        onPointerMove={handleViewportPointerMove}
        onPointerUp={finishViewportPointerPan}
        onPointerCancel={finishViewportPointerPan}
        sx={{
          minHeight: viewportMinHeight,
          width: "100%",
          position: "relative",
          overflow: "auto",
          borderRadius: "1.25rem",
          border: areaTienePlano ? "none" : "1px solid rgba(44, 48, 135, 0.16)",
          backgroundColor: "#ffffff",
          boxShadow: areaTienePlano ? "0 18px 34px rgba(15, 23, 42, 0.12)" : "none",
          cursor: canPan ? (isPanning ? "grabbing" : "grab") : "default",
          touchAction: canPan ? "none" : "auto",
          overscrollBehavior: "contain",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >

        <SoftBox
          sx={{
            position: "relative",
            width: `${scaledCanvasMetrics.width}px`,
            height: `${scaledCanvasMetrics.height}px`,
            backgroundColor: "#ffffff",
          }}
        >
          <SoftBox
            sx={{
              position: "relative",
              width: `${canvasMetrics.width}px`,
              height: `${canvasMetrics.height}px`,
              transform: `scale(${safeZoom})`,
              transformOrigin: "top left",
            }}
          >
            {areaTienePlano ? (
              <img
                src={resolveStoredFileUrl(areaActual.file)}
                alt={areaActual.nombre}
                onLoad={(event) => {
                  const nextRatio = event.currentTarget.naturalWidth
                    ? event.currentTarget.naturalHeight / event.currentTarget.naturalWidth
                    : null;
                  setImageRatio(nextRatio);
                }}
                style={{
                  width: "100%",
                  height: "auto",
                  display: "block",
                  pointerEvents: "none",
                  userSelect: "none",
                }}
              />
            ) : null}

            {mesas.map((mesa, index) => (
              <Mesa
                key={index}
                mesa={mesa}
                moverHabilitado={moverHabilitado}
                setMesasTemporal={setMesasTemporal}
                mesaTemporal={mesasTemporal.find((mesaTemporal) => mesaTemporal.id === mesa.id)}
                handleOpenPedido={handleOpenPedido}
                scale={safeZoom}
              />
            ))}
          </SoftBox>
        </SoftBox>
      </SoftBox>
    </SoftBox>
  );
}

PlanViewport.propTypes = {
  areaActual: PropTypes.object,
  areaTienePlano: PropTypes.bool,
  mesas: PropTypes.array,
  mesasTemporal: PropTypes.array,
  moverHabilitado: PropTypes.bool,
  setMesasTemporal: PropTypes.func,
  handleOpenPedido: PropTypes.func,
  zoom: PropTypes.number,
  mobile: PropTypes.bool,
  tablet: PropTypes.bool,
};

export default PlanViewport;
